
"use client";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";

const navItems = [
  { label: "BUSINESS SETUP", href: "#" },
  { label: "CORPORATE SERVICES", href: "#" },
  { label: "FREE ZONES", href: "#" },
  { label: "VISA SERVICES", href: "#" },
  { label: "PRICING", href: "#" },
];

const slides = [
  {
    bg: "all.webp",
    heading: "Everything Your Business Needs All in One Place",
    description:
      "From company formation to licensing and visas, we make business setup simple, fast, and fully compliant so you can focus on growth.",
    cta: "Get Started",
  },
  {
    bg: "Cost.webp",
    heading: "How Much Does Business Setup in Dubai Cost?",
    description:
      "That depends. Your business activity, location, visas, and shareholders all play a role. Get an instant estimate with our smart calculator.",
    cta: "Calculate Now",
  },
  {
    bg: "World-visa-hero.webp",
    heading: "Hassle-Free UAE & Global Visa Services",
    description:
      "From investor and employment visas to global visa assistance, we handle the complete process quickly and efficiently.",
    cta: "Apply Now",
  },
  {
    bg: "europe.webp",
    heading: "Europe Work Permit & Visit Visa",
    description:
      "Fast, compliant support for Europe work permits and visit visas guidance, documentation and submission handled end-to-end.",
    cta: "Apply Europe Visa",
  },
  {
    bg: "accounts.webp",
    heading: "Simplify Your Accounting & Stay Tax Compliant",
    description:
      "From bookkeeping and VAT filing to corporate tax compliance, our experts keep your finances fully aligned.",
    cta: "Get Expert Help",
  },
  {
    bg: "marketing.webp",
    heading: "Grow Your Business with Powerful Digital Marketing",
    description:
      "From website development and SEO to social media marketing, we help you attract the right customers.",
    cta: "Boost My Business",
  },
];

export const Hero = () => {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const animatingRef = useRef(false);

  const goTo = (i) => {
    if (animatingRef.current || i === current) return;

    animatingRef.current = true;
    setAnimating(true);

    setTimeout(() => {
      setCurrent(i);
      animatingRef.current = false;
      setAnimating(false);
    }, 500);
  };

  // AUTO SLIDE EVERY 5 SEC
  useEffect(() => {
    const interval = setInterval(() => {
      const next = (current + 1) % slides.length;
      goTo(next);
    }, 5000);

    return () => clearInterval(interval);
  }, [current]);

  const handleImageClick = () => {
    const nextIdx = (current + 1) % slides.length;
    goTo(nextIdx);
  };

  const currentSlide = slides[current];
  const nextSlide = slides[(current + 1) % slides.length];
  const thirdSlide = slides[(current + 2) % slides.length];

  const router = useRouter();

  const handleCTA = (cta) => {
    const map = {
      "Get Started": "/contact",
      "Calculate Now": "/calculator",
      "Apply Now": "/services",
      "Apply Europe Visa": "/europe-work-permit",
      "Get Expert Help": "/essential-services/accounting-services",
      "Boost My Business": "/essential-services/digital-marketing",
    };
    const path = map[cta] || "/contact";
    router.push(path);
  };

 return (
  <main style={{ background: "#000", width: "100%", overflowX: "hidden" }}>
    <section
      className="hero-section"
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* BACKGROUND */}
      <img
        src={currentSlide.bg}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center",
          zIndex: 0,
          transition: "0.6s ease",
        }}
      />

      {/* LIGHTER OVERLAY */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          background:
            "linear-gradient(180deg,rgba(0,0,0,0.22) 0%,rgba(0,0,0,0.38) 45%,rgba(0,0,0,0.70) 100%)",
        }}
      />

      {/* CONTENT */}
      <div
        className="hero-content-grid"
        style={{
          position: "relative",
          zIndex: 10,
          flex: 1,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          alignItems: "center",
          gap: "clamp(40px,8vw,120px)",
          padding:
            "clamp(80px,8vh,100px) 0 clamp(80px,8vh,100px) clamp(20px,5vw,120px)",
        }}
      >
        {/* LEFT CONTAINER (Added 'hero-content-left' class here) */}
        <div
          className="hero-content-left"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "18px",
            marginBottom: "80px",
            maxWidth: "650px",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: "clamp(28px, 5vw, 48px)",
              fontWeight: 700,
              lineHeight: 1.2,
              color: "#fff",
              letterSpacing: "-0.02em",
              opacity: animating ? 0 : 1,
              transform: animating ? "translateY(-10px) scale(0.98)" : "translateY(0) scale(1)",
              transition: "opacity 0.45s, transform 0.45s",
            }}
            className="hero-h1"
          >
            {currentSlide.heading}
          </h1>

          <p
            style={{
              margin: 0,
              fontSize: "clamp(14px, 1.2vw, 16px)",
              fontWeight: 400,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.86)",
              maxWidth: "620px",
              opacity: animating ? 0 : 1,
              transform: animating ? "translateY(6px) scale(0.99)" : "translateY(0) scale(1)",
              transition: "opacity 0.45s, transform 0.45s",
            }}
            className="hero-p"
          >
            {currentSlide.description}
          </p>

          {/* BUTTON (Removed 'alignSelf: flex-start' to let media query handle it) */}
          <button
            type="button"
            className="hero-btn"
            onClick={() => handleCTA(currentSlide.cta)}
            style={{
              padding: "12px 28px",
              background: "#b8893b",
              borderRadius: "10px",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              letterSpacing: "0.03em",
              textDecoration: "none",
              transition: "0.3s ease",
              border: "none",
              cursor: "pointer",
            }}
          >
            {currentSlide.cta}
          </button>
        </div>

        {/* RIGHT GALLERY */}
        <div
          className="gallery-panel"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          {/* MAIN IMAGE */}
          <div
            onClick={handleImageClick}
            className="gallery-img-1"
            style={{
              overflow: "hidden",
              zIndex: 3,
              cursor: "pointer",
              boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
            }}
          >
            <img
              src={currentSlide.bg}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </div>

          {/* SECOND */}
          <div
            onClick={handleImageClick}
            className="gallery-img-2"
            style={{
              overflow: "hidden",
              zIndex: 2,
              cursor: "pointer",
              boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
            }}
          >
            <img
              src={nextSlide.bg}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </div>

          {/* THIRD */}
          <div
            onClick={handleImageClick}
            className="gallery-img-3"
            style={{
              overflow: "hidden",
              zIndex: 1,
              cursor: "pointer",
              opacity: 0.9,
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            }}
          >
            <img
              src={thirdSlide.bg}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </div>
        </div>
      </div>

    </section>

    {/* MOBILE MENU */}
    {menuOpen && (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.96)",
          zIndex: 50,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "32px",
        }}
      >
        <button
          onClick={() => setMenuOpen(false)}
          style={{
            position: "absolute",
            top: "24px",
            right: "24px",
            background: "none",
            border: "none",
            color: "#fff",
            fontSize: "28px",
            cursor: "pointer",
          }}
        >
          ✕
        </button>

        {navItems.map((item) => (
          <a
            key={item.label}
            href={item.href}
            style={{
              fontWeight: 700,
              fontSize: "18px",
              color: "#fff",
              textDecoration: "none",
              letterSpacing: "0.08em",
            }}
          >
            {item.label}
          </a>
        ))}
      </div>
    )}

    <style>{`
@keyframes slideInFromRight {
  from { transform: translateX(100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

/* MOBILE */
@media (max-width: 900px) {
  .hero-section {
    height: min(48vh, 640px) !important;
    margin-top: 72px !important;
  }

  .gallery-panel {
    display: none !important;
  }

  .hero-content-grid {
    padding: 24px 20px 32px !important;
    grid-template-columns: 1fr !important;
  }

  .hero-content-left {
    align-items: flex-start !important;
    max-width: 100% !important;
    margin-bottom: 0px !important;
    gap: 14px !important;
  }

  .hero-h1 {
    font-size: 22px !important;
    line-height: 1.15 !important;
    text-align: left !important;
    max-width: 100% !important;
  }

  .hero-p {
    font-size: 14px !important;
    line-height: 1.7 !important;
    text-align: left !important;
    max-width: 100% !important;
    margin: 0 !important;
  }

  .hero-btn {
    align-self: flex-start !important;
  }
}

/* LAPTOP */
@media (min-width: 901px) {
  .hero-btn {
    align-self: flex-start !important; /* Desktop par wapis left side par le ayega */
  }
}

@media (min-width: 901px) and (max-width: 1200px) {
  .hero-section {
    height: 92vh !important;
  }

  .hero-content-grid {
    padding: 36px 24px 42px 24px !important;
    gap: 24px !important;
    grid-template-columns: 1fr 300px !important;
  }

  .hero-content-left {
    margin-bottom: 0 !important;
    max-width: 520px !important;
    gap: 14px !important;
  }

  .hero-h1 {
    font-size: 32px !important;
    line-height: 1.16 !important;
    max-width: 480px !important;
  }

  .hero-p {
    font-size: 14px !important;
    line-height: 1.65 !important;
    max-width: 460px !important;
  }

  .gallery-panel {
    height: 460px !important;
    gap: 18px !important;
  }

  .gallery-img-1 {
      margin-left: -120px !important;

    width: 210px !important;
    height: 360px !important;
    border-radius: 20px !important;
  }

  .gallery-img-2 {
    width: 160px !important;
    height: 240px !important;
    margin-left: -0px !important;
    margin-top: 90px !important;
    border-radius: 16px !important;
  }

  .gallery-img-3 {
    width: 140px !important;
    height: 220px !important;
    margin-left: -0px !important;
    margin-right: -120px !important;
    margin-top: 90px !important;
    border-radius: 14px !important;
  }
}

@media (min-width: 1201px) and (max-width: 1600px) {
  .hero-h1 { 
    font-size: clamp(40px, 4vw, 60px);
    line-height: 1.08;
    max-width: 700px;
  }

  .hero-p { 
    font-size: clamp(15px, 1vw, 18px);
    line-height: 1.75;
    max-width: 620px;
    color: rgba(255,255,255,0.88);
  }

  .gallery-panel { 
    height: clamp(400px, 50vw, 1400px); 
    gap: clamp(10px, 2vw, 50px); 
  }

  .gallery-img-1 { 
    width: clamp(220px, 18vw, 300px); 
    height: clamp(380px, 20vh, 650px); 
    border-radius: clamp(16px, 1.8vw, 32px); 
  }

  .gallery-img-2 { 
    width: clamp(180px, 14vw, 240px); 
    height: clamp(200px, 10vh, 500px); 
    border-radius: clamp(14px, 1.6vw, 26px); 
    margin-left: -clamp(40px, 4vw, 80px); 
    margin-top: 160px; 
  }

  .gallery-img-3 { 
    width: clamp(140px, 10vw, 240px); 
    height: clamp(200px, 10vh, 500px); 
    border-radius: clamp(12px, 1.4vw, 22px); 
    margin-left: -clamp(40px, 4vw, 80px); 
    margin-right: -120px !important; 
    margin-top: 150px; 
  }
}

/* LARGE SCREENS */
@media (min-width: 1600px) {
  .hero-h1 { 
    font-size: clamp(58px, 4vw, 82px) !important;
    line-height: 1.05 !important;
    max-width: 850px !important;
  }

  .hero-p { 
    font-size: clamp(17px, 1.2vw, 22px) !important;
    line-height: 1.8 !important;
    max-width: 700px !important;
    color: rgba(255,255,255,0.88) !important;
  }

  .gallery-panel { 
    height: 800px !important; 
    gap: 60px !important; 
  }

  .gallery-img-1 { 
    width: 340px !important; 
    height: 600px !important; 
    border-radius: 32px !important; 
  }

  .gallery-img-2 { 
    width: 300px !important; 
    height: 400px !important; 
    margin-left: -40px !important; 
    margin-top: 160px !important; 
    border-radius: 28px !important; 
  }

  .gallery-img-3 { 
    width: 300px !important; 
    height: 400px !important; 
    margin-left: -40px !important; 
    margin-right: -150px !important; 
    margin-top: 160px !important; 
    border-radius: 24px !important; 
  }
}

/* ULTRA WIDE */
@media (min-width: 2000px) {
  .hero-h1 { 
    font-size: 80px !important;
    line-height: 1.02 !important;
    max-width: 950px !important;
  }

  .hero-p { 
    font-size: 24px !important;
    line-height: 1.9 !important;
    max-width: 760px !important;
  }

  .gallery-panel { 
    height: 900px !important; 
    gap: 80px !important; 
  }

  .gallery-img-1 { 
    width: 420px !important; 
    height: 700px !important; 
    border-radius: 40px !important; 
  }

  .gallery-img-2 { 
    width: 240px !important; 
    height: 500px !important; 
    margin-left: -50px !important; 
    margin-top: 140px !important; 
    border-radius: 32px !important; 
  }

  .gallery-img-3 { 
    width: 280px !important; 
    height: 500px !important; 
    margin-left: -50px !important; 
    margin-right: -140px !important; 
    margin-top: 140px !important; 
    border-radius: 28px !important; 
  }
}
`}</style>
  </main>
);
};

export default Hero;